if (!customElements.get('share-space-hero-form')) {
  class ShareSpaceHeroForm extends HTMLElement {
    constructor() {
      super();
      this.files = [];
      this.maxFiles = parseInt(this.getAttribute('data-max-files') || '4', 10);
      this.maxSize = 10 * 1024 * 1024;
      this.endpoint = this.getAttribute('data-form-endpoint') || '';
    }

    connectedCallback() {
      this.dropzone = this.querySelector('[data-dropzone]');
      this.fileInput = this.querySelector('[data-file-input]');
      this.previewsEl = this.querySelector('[data-previews]');
      this.fileCountEl = this.querySelector('[data-file-count]');
      this.roomInput = this.querySelector('[data-room-input]');
      this.nameInput = this.querySelector('[data-name-input]');
      this.emailInput = this.querySelector('[data-email-input]');
      this.errorEl = this.querySelector('[data-error]');
      this.submitBtn = this.querySelector('[data-submit]');
      this.submitText = this.querySelector('[data-submit-text]');
      this.spinnerEl = this.querySelector('[data-spinner]');
      this.formState = this.querySelector('[data-state="form"]');
      this.confirmState = this.querySelector('[data-state="confirmation"]');
      this.form = this.querySelector('form');
      this.defaultSubmitLabel =
        this.getAttribute('data-submit-label') || 'Submit Your Space';

      this._bindEvents();
      this._bindScrollLinks();
    }

    _bindEvents() {
      this.dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        this.dropzone.classList.add('sysv2-hero__dropzone--active');
      });

      this.dropzone.addEventListener('dragleave', () => {
        this.dropzone.classList.remove('sysv2-hero__dropzone--active');
      });

      this.dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        this.dropzone.classList.remove('sysv2-hero__dropzone--active');
        this._addFiles(e.dataTransfer.files);
      });

      this.fileInput.addEventListener('change', () => {
        this._addFiles(this.fileInput.files);
        this.fileInput.value = '';
      });

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._handleSubmit();
      });
    }

    _bindScrollLinks() {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      document.querySelectorAll('[data-scroll-to]').forEach((link) => {
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href');
          if (href && href.startsWith('#')) {
            e.preventDefault();
            const target = document.querySelector(href);
            if (target) {
              const smooth = !reduceMotion.matches;
              target.scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: 'start',
              });
            }
          }
        });
      });
    }

    _addFiles(fileList) {
      const incoming = Array.from(fileList);
      for (const file of incoming) {
        if (this.files.length >= this.maxFiles) {
          this._showError(`Maximum ${this.maxFiles} photos allowed.`);
          break;
        }
        if (file.size > this.maxSize) {
          this._showError(`"${file.name}" exceeds 10MB limit.`);
          continue;
        }
        if (!file.type.match(/^image\/(jpeg|png|webp|heic)$/)) {
          this._showError(`"${file.name}" is not a supported image format.`);
          continue;
        }
        this.files.push(file);
      }
      this._renderPreviews();
    }

    _renderPreviews() {
      this.previewsEl.innerHTML = '';
      this.files.forEach((file, idx) => {
        const div = document.createElement('div');
        div.className = 'sysv2-hero__preview';
        const img = document.createElement('img');
        const objectUrl = URL.createObjectURL(file);
        img.src = objectUrl;
        img.alt = file.name;
        img.onload = () => URL.revokeObjectURL(objectUrl);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sysv2-hero__preview-remove';
        btn.setAttribute('aria-label', `Remove ${file.name}`);
        btn.textContent = '\u00D7';
        btn.addEventListener('click', () => this._removeFile(idx));
        div.appendChild(img);
        div.appendChild(btn);
        this.previewsEl.appendChild(div);
      });
      this.fileCountEl.textContent =
        this.files.length > 0
          ? `${this.files.length} of ${this.maxFiles} photos selected`
          : '';
      this._clearError();
    }

    _removeFile(idx) {
      this.files.splice(idx, 1);
      this._renderPreviews();
    }

    _validate() {
      if (this.files.length === 0) {
        this._showError('Please upload at least one photo.');
        return false;
      }
      if (!this.roomInput.value) {
        this._showError('Please select a room type.');
        this.roomInput.classList.add('sysv2-hero__select--error');
        return false;
      }
      if (!this.nameInput.value.trim()) {
        this._showError('Please enter your name.');
        this.nameInput.classList.add('sysv2-hero__input--error');
        return false;
      }
      const email = this.emailInput.value.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this._showError('Please enter a valid email address.');
        this.emailInput.classList.add('sysv2-hero__input--error');
        return false;
      }
      return true;
    }

    async _handleSubmit() {
      this._clearError();
      this.querySelectorAll(
        '.sysv2-hero__input--error, .sysv2-hero__select--error'
      ).forEach((el) => {
        el.classList.remove('sysv2-hero__input--error', 'sysv2-hero__select--error');
      });

      if (!this._validate()) return;

      this._setLoading(true);

      const formData = new FormData();
      formData.append('name', this.nameInput.value.trim());
      formData.append('email', this.emailInput.value.trim());
      formData.append('room', this.roomInput.value);
      this.files.forEach((file) => formData.append('files', file));

      try {
        if (this.endpoint) {
          const res = await fetch(this.endpoint, {
            method: 'POST',
            body: formData,
          });
          if (!res.ok) {
            let message = `Something went wrong (${res.status}). Please try again.`;
            try {
              const data = await res.json();
              if (data && typeof data.error === 'string' && data.error.length) {
                message = data.error;
              }
            } catch (_) {
              /* non-JSON body */
            }
            throw new Error(message);
          }
        }

        this._trackEvent('share_space_submit', {
          room: this.roomInput.value,
          photo_count: this.files.length,
        });

        this._showConfirmation();
      } catch (err) {
        const fallback = 'Something went wrong. Please try again.';
        this._showError(
          err && typeof err.message === 'string' && err.message.length
            ? err.message
            : fallback
        );
        console.error('Share Space submission error:', err);
      } finally {
        this._setLoading(false);
      }
    }

    _setLoading(loading) {
      this.submitBtn.disabled = loading;
      if (loading) {
        this.submitBtn.classList.add('sysv2-hero__submit--loading');
        this.submitText.textContent = 'Uploading\u2026';
      } else {
        this.submitBtn.classList.remove('sysv2-hero__submit--loading');
        this.submitText.textContent = this.defaultSubmitLabel;
      }
    }

    _showConfirmation() {
      this.formState.hidden = true;
      this.confirmState.hidden = false;
    }

    _showError(msg) {
      this.errorEl.textContent = msg;
    }

    _clearError() {
      this.errorEl.textContent = '';
    }

    _trackEvent(eventName, params) {
      try {
        if (typeof fbq === 'function') {
          fbq('trackCustom', eventName, params);
        }
      } catch (e) {
        /* silent */
      }

      try {
        if (typeof gtag === 'function') {
          gtag('event', eventName, params);
        }
      } catch (e) {
        /* silent */
      }

      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: eventName, ...params });
      } catch (e) {
        /* silent */
      }
    }
  }

  customElements.define('share-space-hero-form', ShareSpaceHeroForm);
}
